import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { PublicLayout } from '@/components/PublicLayout';
import { fetchPublicListings } from '@/utils/api';
import { MapPin, Euro, Calendar, Home, Eye } from 'lucide-react';

const Listings = () => {
  const { t, language } = useLanguage();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadListings();
  }, []);

  const loadListings = async () => {
    try {
      const data = await fetchPublicListings();
      setListings(data);
    } catch (error) {
      console.error('Error loading listings:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicLayout>
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight text-slate-900 mb-6">
              {t.listings.title}
            </h1>
            <p className="text-xl text-slate-600">{t.listings.subtitle}</p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block w-12 h-12 border-4 border-[#127abb] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : listings.length === 0 ? (
            <div data-testid="no-listings" className="max-w-4xl mx-auto">
              {/* Empty State Hero */}
              <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-3xl p-8 md:p-12 text-center mb-12">
                <div className="w-24 h-24 bg-white rounded-2xl shadow-lg flex items-center justify-center mx-auto mb-6">
                  <Home className="text-[#127abb]" size={48} />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
                  {language === 'fr' 
                    ? 'Nouvelles annonces bientôt disponibles' 
                    : 'New listings coming soon'}
                </h2>
                <p className="text-slate-600 text-lg mb-8 max-w-xl mx-auto">
                  {language === 'fr'
                    ? 'Notre équipe travaille activement pour vous proposer les meilleures offres de logement à Lyon. Revenez bientôt !'
                    : 'Our team is actively working to bring you the best housing offers in Lyon. Check back soon!'}
                </p>
                <Link to="/contact">
                  <button className="bg-[#127abb] hover:bg-[#0e6292] text-white font-semibold px-8 py-4 rounded-full transition-all hover:scale-105 shadow-lg">
                    {language === 'fr' ? 'Contactez-nous pour vos besoins' : 'Contact us for your needs'}
                  </button>
                </Link>
              </div>

              {/* Info Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                <div className="bg-white border border-slate-200 rounded-2xl p-6 text-center hover:shadow-lg transition-shadow">
                  <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <MapPin className="text-[#127abb]" size={28} />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">
                    {language === 'fr' ? 'Quartiers variés' : 'Various neighborhoods'}
                  </h3>
                  <p className="text-sm text-slate-600">
                    {language === 'fr'
                      ? 'Studios, T1, T2, colocations dans tous les arrondissements de Lyon'
                      : 'Studios, 1-bed, 2-bed, shared housing in all Lyon districts'}
                  </p>
                </div>
                <div className="bg-white border border-slate-200 rounded-2xl p-6 text-center hover:shadow-lg transition-shadow">
                  <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Euro className="text-green-600" size={28} />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">
                    {language === 'fr' ? 'Budget étudiant' : 'Student budget'}
                  </h3>
                  <p className="text-sm text-slate-600">
                    {language === 'fr'
                      ? 'Des logements adaptés aux budgets étudiants, éligibles aux aides CAF'
                      : 'Housing adapted to student budgets, eligible for CAF assistance'}
                  </p>
                </div>
                <div className="bg-white border border-slate-200 rounded-2xl p-6 text-center hover:shadow-lg transition-shadow">
                  <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Calendar className="text-purple-600" size={28} />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">
                    {language === 'fr' ? 'Disponibilité flexible' : 'Flexible availability'}
                  </h3>
                  <p className="text-sm text-slate-600">
                    {language === 'fr'
                      ? 'Locations courte et longue durée selon vos besoins académiques'
                      : 'Short and long-term rentals according to your academic needs'}
                  </p>
                </div>
              </div>

              {/* CTA Section */}
              <div className="bg-slate-900 rounded-3xl p-8 md:p-10 text-center">
                <h3 className="text-xl md:text-2xl font-bold text-white mb-4">
                  {language === 'fr' 
                    ? 'Vous cherchez un logement à Lyon ?' 
                    : 'Looking for housing in Lyon?'}
                </h3>
                <p className="text-slate-300 mb-6 max-w-lg mx-auto">
                  {language === 'fr'
                    ? 'Décrivez-nous vos critères et notre équipe vous accompagnera dans votre recherche.'
                    : 'Tell us your criteria and our team will help you in your search.'}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/contact">
                    <button className="bg-[#127abb] hover:bg-[#0e6292] text-white font-semibold px-6 py-3 rounded-full transition-all">
                      {language === 'fr' ? 'Faire une demande' : 'Submit a request'}
                    </button>
                  </Link>
                  <Link to="/services">
                    <button className="bg-white/10 hover:bg-white/20 text-white font-semibold px-6 py-3 rounded-full transition-all border border-white/30">
                      {language === 'fr' ? 'Nos services' : 'Our services'}
                    </button>
                  </Link>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {listings.map((listing) => (
                <div
                  key={listing.listing_id}
                  data-testid={`listing-card-${listing.listing_id}`}
                  className="bg-white border border-stone-200 overflow-hidden rounded-2xl hover:-translate-y-2 transition-all duration-300 shadow-lg hover:shadow-2xl group"
                >
                  <div className="relative h-48 overflow-hidden bg-slate-200">
                    {listing.images && listing.images.length > 0 ? (
                      <>
                        <img
                          src={listing.images[0]}
                          alt={language === 'fr' ? listing.title_fr : listing.title_en}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                        {/* Overlay with zoom icon on hover */}
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center">
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 backdrop-blur-sm rounded-full p-3">
                            <Eye className="text-[#127abb]" size={24} />
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Home className="text-slate-400" size={48} />
                      </div>
                    )}
                    <div className="absolute top-4 right-4 bg-white px-3 py-1 rounded-full text-sm font-medium text-[#127abb] shadow-lg">
                      {listing.type}
                    </div>
                    {listing.images && listing.images.length > 1 && (
                      <div className="absolute bottom-4 right-4 bg-black/60 backdrop-blur-sm text-white px-3 py-1 rounded-full text-xs font-medium">
                        +{listing.images.length - 1} photo{listing.images.length - 1 > 1 ? 's' : ''}
                      </div>
                    )}
                  </div>

                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-slate-900 mb-2 line-clamp-1">
                      {language === 'fr' ? listing.title_fr : listing.title_en}
                    </h3>
                    <p className="text-slate-600 mb-4 line-clamp-2">
                      {language === 'fr' ? listing.description_fr : listing.description_en}
                    </p>

                    <div className="space-y-2 mb-4">
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <MapPin size={16} className="text-[#127abb] flex-shrink-0" />
                        <span className="line-clamp-1">{listing.location}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Euro size={16} className="text-[#127abb] flex-shrink-0" />
                        <span>
                          {t.listings.rent}: <span className="font-semibold text-slate-900">{listing.rent}{t.listings.perMonth}</span>
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Calendar size={16} className="text-[#127abb] flex-shrink-0" />
                        <span className="line-clamp-1">
                          {t.listings.available}: {listing.available_from}
                        </span>
                      </div>
                    </div>

                    {listing.features && listing.features.length > 0 && (
                      <div className="border-t border-slate-200 pt-4 mb-4">
                        <p className="text-xs font-medium text-slate-500 mb-2">
                          {t.listings.features}:
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {listing.features.slice(0, 3).map((feature, index) => (
                            <span
                              key={index}
                              className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded-full"
                            >
                              {feature}
                            </span>
                          ))}
                          {listing.features.length > 3 && (
                            <span className="text-xs bg-primary-50 text-[#127abb] px-2 py-1 rounded-full font-medium">
                              +{listing.features.length - 3}
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    <Link to={`/listings/${listing.listing_id}`}>
                      <button className="w-full mt-4 py-3 px-4 bg-[#127abb] hover:bg-[#0e6292] text-white rounded-full font-medium transition-all duration-300 shadow-md hover:shadow-xl">
                        Voir les détails
                      </button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </PublicLayout>
  );
};

export default Listings;